import { Calendar } from "primereact/calendar";
import { UseFieldArrayReturn } from "react-hook-form";
import { IconType } from "../../../icons/Icon";
import { IconButton } from "../../button/iconButton/IconButton";
import { Controller, useFormContext } from "react-hook-form";
import styles from "./WorkingHourDay.module.scss";
import { DayKey, IWorkingHoursFormModel } from "./models/models";
import { classNames } from "primereact/utils";

interface IWorkingHourDayProps {
  dayKey: DayKey;
  dayName: string;
  fieldArray: UseFieldArrayReturn<IWorkingHoursFormModel, DayKey, "id">;
  isStudio: boolean;
  onAddSlot: () => void;
  onRemoveSlot: (index: number) => void;
}

export const WorkingHourDay = ({
  dayKey,
  dayName,
  fieldArray,
  isStudio,
  onAddSlot,
  onRemoveSlot,
}: IWorkingHourDayProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<IWorkingHoursFormModel>();

  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const formatTimeToString = (date: Date | null): string => {
    if (!date) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const hasError = !!errors[dayKey];

  const closedTitleText = isStudio ? "Обектът е затворен" : "Служителят почива";
  const closedDescriptionText = isStudio
    ? "Ако искате да го маркирате като 'Отворен', добавете нов работен диапазон от плюс бутона."
    : "Ако искате да го маркирате като 'Работен ден', добавете нов диапазон от плюс бутона.";

  return (
    <div className={styles.root}>
      <div
        className={classNames(styles.root_header, {
          [styles.root_header_error]: hasError,
        })}
      >
        {dayName}
        <IconButton
          className={styles.root_header_plus}
          iconType={IconType.PlusIcon}
          onClick={onAddSlot}
        />
      </div>

      <div className={styles.root_content}>
        {fieldArray.fields.length === 0 ? (
          <div className={styles.root_content_closed}>
            <span>{closedTitleText}</span>
            <span className={styles.root_content_closed_disclaimer}>
              {closedDescriptionText}
            </span>
          </div>
        ) : (
          fieldArray.fields.map((field, index) => (
            <div key={field.id} className={styles.root_content_wrapper}>
              <div className={styles.root_content_left}>
                <Controller
                  name={`${dayKey}.${index}.startTime`}
                  control={control}
                  render={({ field: startTimeField }) => (
                    <Calendar
                      timeOnly
                      value={
                        startTimeField.value
                          ? parseTimeString(startTimeField.value)
                          : null
                      }
                      onChange={(e) => {
                        const timeString = formatTimeToString(e.value as Date);
                        startTimeField.onChange(timeString);
                      }}
                      showTime
                      hourFormat="24"
                      stepMinute={15}
                    />
                  )}
                />

                <span>до</span>

                <Controller
                  name={`${dayKey}.${index}.endTime`}
                  control={control}
                  render={({ field: endTimeField }) => (
                    <Calendar
                      timeOnly
                      value={
                        endTimeField.value
                          ? parseTimeString(endTimeField.value)
                          : null
                      }
                      onChange={(e) => {
                        const timeString = formatTimeToString(e.value as Date);
                        endTimeField.onChange(timeString);
                      }}
                      showTime
                      hourFormat="24"
                      stepMinute={15}
                    />
                  )}
                />
              </div>

              <IconButton
                className={styles.root_content_delete}
                iconType={IconType.DeleteIcon}
                iconHeight="25px"
                onClick={() => onRemoveSlot(index)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};
